import { useRef, useLayoutEffect, useCallback } from 'react';
import { useZoomSafeContext } from './zoom-context';

export interface ScrollPreservationOptions {
  /** Whether to preserve scroll position during zoom changes */
  preserveScroll?: boolean;
  /** Use top-anchored zoom instead of proportional scroll adjustment */
  topAnchoredZoom?: boolean;
  /** Custom scroll position calculation function */
  customScrollCalculation?: (currentScroll: number, previousZoom: number, newZoom: number) => number;
}

export const useScrollPreservation = ({
  preserveScroll = true,
  topAnchoredZoom = true,
  customScrollCalculation,
}: ScrollPreservationOptions = {}) => {
  const { zoomLevel, pixelsPerMinute } = useZoomSafeContext();
  const scrollRef = useRef<HTMLDivElement>(null);

  const previousZoomLevelRef = useRef(zoomLevel);
  const previousPixelsPerMinuteRef = useRef(pixelsPerMinute);
  const isZoomingRef = useRef(false);

  // Capture scrollTop before the browser adjusts it for the new layout
  const capturedScrollTopRef = useRef<number | null>(null);
  if (
    previousZoomLevelRef.current !== zoomLevel &&
    previousPixelsPerMinuteRef.current !== pixelsPerMinute &&
    scrollRef.current
  ) {
    capturedScrollTopRef.current = scrollRef.current.scrollTop;
  }

  // Top-anchored zoom implementation
  const performTopAnchoredZoom = useCallback(
    (container: HTMLElement, previousPixelsPerMinute: number, newPixelsPerMinute: number) => {
      const scrollTop = capturedScrollTopRef.current ?? container.scrollTop;

      // Calculate zoom ratio and new scroll position
      const zoomRatio = newPixelsPerMinute / previousPixelsPerMinute;
      const newScrollTop = scrollTop * zoomRatio;

      // Set zooming flag
      isZoomingRef.current = true;

      // Instant scroll positioning
      container.scrollTop = newScrollTop;
      isZoomingRef.current = false;
    },
    []
  );

  // Preserve scroll position when zoom changes
  useLayoutEffect(() => {
    if (!preserveScroll) {
      previousZoomLevelRef.current = zoomLevel;
      previousPixelsPerMinuteRef.current = pixelsPerMinute;
      return;
    }

    const container = scrollRef.current;
    if (!container) return;

    const previousZoomLevel = previousZoomLevelRef.current;
    const previousPixelsPerMinute = previousPixelsPerMinuteRef.current;

    // Only adjust scroll if zoom actually changed
    if (previousZoomLevel !== zoomLevel && previousPixelsPerMinute !== pixelsPerMinute) {
      if (topAnchoredZoom) {
        performTopAnchoredZoom(container, previousPixelsPerMinute, pixelsPerMinute);
      } else if (customScrollCalculation) {
        const newScrollTop = customScrollCalculation(container.scrollTop, previousZoomLevel, zoomLevel);
        container.scrollTop = newScrollTop;
      } else {
        // Default proportional adjustment
        const zoomRatio = pixelsPerMinute / previousPixelsPerMinute;
        container.scrollTop = container.scrollTop * zoomRatio;
      }
    }

    // Update refs for next comparison
    previousZoomLevelRef.current = zoomLevel;
    previousPixelsPerMinuteRef.current = pixelsPerMinute;
  }, [
    zoomLevel,
    pixelsPerMinute,
    preserveScroll,
    topAnchoredZoom,
    customScrollCalculation,
    scrollRef,
    performTopAnchoredZoom,
  ]);

  return {
    scrollRef,
    isZooming: isZoomingRef.current,
  };
};
